import { FC, useState } from 'react'
import { Button, Input, InputNumber, Space, Popconfirm } from 'antd'
import { SaveOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { Formik, Form, Field, FieldProps } from 'formik'
import { ICustomerWithPricing } from '@/supabase/Customer/customer.interface'
import {
  ICustomerPricingFormValues,
  customerPricingValidationSchema,
  getInitialFormValues,
} from '../interfaces'

// CSS for financial input styling
const financialInputStyles = `
  .financial-input .ant-input-number-input {
    text-align: right !important;
  }
`

interface IInlineEditableRowProps {
  customer: ICustomerWithPricing
  onSave: (
    customer: ICustomerWithPricing,
    formData: ICustomerPricingFormValues
  ) => Promise<void>
  onCancel: () => void
  onDelete: (customerId: string) => Promise<void>
  loading?: boolean
}

export const InlineEditableRow: FC<IInlineEditableRowProps> = ({
  customer,
  onSave,
  onCancel,
  onDelete,
  loading = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (values: ICustomerPricingFormValues) => {
    setIsSubmitting(true)
    try {
      await onSave(customer, values)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    await onDelete(customer.id)
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: financialInputStyles }} />
      <div className='bg-blue-50 p-4 rounded-lg'>
      <Formik
        initialValues={getInitialFormValues(customer)}
        validationSchema={customerPricingValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, errors, touched, isValid }) => (
          <Form>
            {/* Form Fields */}
            <div className='grid grid-cols-9 gap-3 items-start mb-5'>
              {/* Name Field */}
              <div className='col-span-2'>
                <label className='block text-xs font-medium text-gray-700 mb-1'>
                  Customer Name
                </label>
                <Field name='name'>
                  {({ field }: FieldProps) => (
                    <Input
                      {...field}
                      placeholder='Customer Name'
                      disabled={loading || isSubmitting}
                      status={errors.name && touched.name ? 'error' : ''}
                      size='small'
                    />
                  )}
                </Field>
                {errors.name && touched.name && (
                  <div className='text-red-500 text-xs mt-1'>{errors.name}</div>
                )}
              </div>

              {/* Large Ice Price */}
              <div className='col-span-2'>
                <label className='block text-xs font-medium text-gray-700 mb-1'>
                  ใหญ่
                </label>
                <Field name='largeIceCubePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.largeIceCubePriceTHB &&
                        touched.largeIceCubePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('largeIceCubePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.largeIceCubePriceTHB &&
                  touched.largeIceCubePriceTHB && (
                    <div className='text-red-500 text-xs mt-1'>
                      {errors.largeIceCubePriceTHB}
                    </div>
                  )}
              </div>

              {/* Small Ice Price */}
              <div className='col-span-2'>
                <label className='block text-xs font-medium text-gray-700 mb-1'>
                  เล็ก
                </label>
                <Field name='smallIceCubePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.smallIceCubePriceTHB &&
                        touched.smallIceCubePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('smallIceCubePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.smallIceCubePriceTHB &&
                  touched.smallIceCubePriceTHB && (
                    <div className='text-red-500 text-xs mt-1'>
                      {errors.smallIceCubePriceTHB}
                    </div>
                  )}
              </div>

              {/* Crushed Ice Price */}
              <div className='col-span-2'>
                <label className='block text-xs font-medium text-gray-700 mb-1'>
                  โม่
                </label>
                <Field name='crushedIcePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.crushedIcePriceTHB && touched.crushedIcePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('crushedIcePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.crushedIcePriceTHB && touched.crushedIcePriceTHB && (
                  <div className='text-red-500 text-xs mt-1'>
                    {errors.crushedIcePriceTHB}
                  </div>
                )}
              </div>

              {/* Empty space */}
            </div>

            {/* Action Buttons - Separate row, far right */}
            <div className='flex justify-end'>
              <Space size='small'>
                <Popconfirm
                  title='Delete Customer'
                  description='Are you sure you want to delete this customer?'
                  onConfirm={handleDelete}
                  okText='Delete'
                  cancelText='Cancel'
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size='small'
                    disabled={isSubmitting || loading}
                  >
                    Delete
                  </Button>
                </Popconfirm>
                <Button
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={onCancel}
                  disabled={isSubmitting || loading}
                >
                  Cancel
                </Button>

                <Button
                  type='primary'
                  icon={<SaveOutlined />}
                  size='small'
                  htmlType='submit'
                  loading={isSubmitting}
                  disabled={!isValid || loading}
                >
                  Save
                </Button>
              </Space>
            </div>
          </Form>
        )}
      </Formik>
      </div>
    </>
  )
}
